import { FormControl, FormControlLabel } from '@fox/atoms/src/form-composites/form-control';
import { BrandMath, CurrencyMinor, ZERO_CENTS } from '@fox/atoms/src/inputs/currency-input';
import { LabeledCheckbox } from '@fox/atoms/src/labeled-checkbox/index';
import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { MuiRadio, MuiRadioGroup } from '@fox/atoms/src/mui-radio/index';
import { Select } from '@fox/atoms/src/select/index';
import { Skeleton } from '@fox/atoms/src/skeleton/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import { IBlockDefinition, IBlockProps } from '@fox/blocks/contracts/block';
import { FormFinancialLineItem, INTERVAL_NAMESLY } from '@fox/finance/src/contracts';
import { formatCurrencyFromMinorUnits, intervalDisplayShort } from '@fox/finance/src/helpers';
import { FlexColumn, FlexColumnSpaced, SpaceBetweenRow, SpaceEvenRowSmallGap } from '@fox/layout/src/flex';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import 'react-phone-number-input/style.css';
import Sqids from 'sqids';
import { PAYMENT_BLOCK_ANSWER_TYPE, SUBSCRIPTION_BLOCK_TYPE } from '../../../form-block-types';
import { findRenderDependencies } from '../../../utils';
import { useFormFinancialContext } from '../../form-financial-context/form-financial-context';
import { FormRenderMode, QuestionStyleTypes } from '../../form-runtime-context/contracts';
import { useFormRuntimeContext, useQuestionStyle } from '../../form-runtime-context/form-runtime-context';
import { RequiredAsterisk } from '../../required-asterisk/required-asterisk';
import { useFormBlockReRenderer } from '../../use-form-block-re-renderer';
import { useConditionEvaluator } from '../form-block/conditionals/helpers';
import { checkSuccessfulPayment } from '../payment-block/helpers';
import { calculateLineItemsTotal } from '../ticket-block/helpers';
import { buildProvidedData, BuildStylesType, Fee, FieldPropsType, Plan, SubscriptionAnswer, SubscriptionFieldMeta } from './contracts';
import {
	buildStyles,
	checkIsCompatiblePlanOption,
	createPlanOptionLabel,
	DEFAULT_SUBSCRIPTION_BLOCK_METADATA,
	encodePlanOptionLabelId,
	findAllPlanOptions,
	findNewOptions,
	getAllowedFees
} from './helpers';

const sqids = new Sqids();

export const SubscriptionBlock = ({ props, id, token }: IBlockProps<FieldPropsType> & { token?: string }) => {
	const { fetchPlans, fetchCurrencies, prorateFee } = useFormFinancialContext();
	const { inLocalSurveyMode, formRenderMode, getAnswers, getAnswer, answerQuestion, sections } = useFormRuntimeContext();
	const answers = getAnswers();
	const dependencies = findRenderDependencies(sections, [PAYMENT_BLOCK_ANSWER_TYPE]);
	useFormBlockReRenderer([props.name, ...dependencies], props);
	const labelStyle = useQuestionStyle(QuestionStyleTypes.label, {}, id);
	const answerStyle = useQuestionStyle(QuestionStyleTypes.answer, {}, id);
	const { labelStyles, answerStyles } = buildStyles(labelStyle, answerStyle);
	const fieldMeta: SubscriptionFieldMeta = props.metadataJson ? JSON.parse(props.metadataJson) : DEFAULT_SUBSCRIPTION_BLOCK_METADATA;

	const {
		data: plans,
		isLoading: plansLoading,
		isError: plansError
	} = useQuery({
		queryKey: ['fetchplans', fieldMeta.allowedPlans, token],
		queryFn: async () => {
			const result = await fetchPlans({ ids: fieldMeta.allowedPlans, token });
			return result ?? [];
		},
		staleTime: Infinity,
		enabled: formRenderMode === FormRenderMode.runtime ? !!token || inLocalSurveyMode : true
	});

	const {
		data: currencies,
		isLoading: currenciesLoading,
		isError: currenciesError
	} = useQuery({
		queryKey: ['fetchCurrencies'],
		queryFn: async () => {
			const result = await fetchCurrencies(token);
			if (!result?.default) throw Error('Unable to fetch supported currencies');
			return result;
		},
		staleTime: Infinity
	});

	const hasSuccessfulPayment = checkSuccessfulPayment(answers ?? []);
	const currentAnswer = getAnswer(props.name);
	const parsedAnswers: SubscriptionAnswer = tryParseJSONObject(currentAnswer?.answer?.answerJson, {});
	const decoded = fieldMeta.allowedPlans?.length ? sqids.decode(fieldMeta.allowedPlans) : [];
	const allowedPlans = plans?.filter(plan => decoded?.some(allowedId => allowedId === plan.id)) ?? [];
	const selectedPlan = plans?.find(p => p.planKey === parsedAnswers.planKey);
	const selectedPlanIntervals: Plan['fees'] = plans?.find(p => p.planKey === parsedAnswers.planKey)?.fees ?? [];
	const selectedPlanAvailableIntervals = selectedPlanIntervals.filter(f =>
		fieldMeta.fees?.restrict && selectedPlan?.id !== undefined ? fieldMeta.fees.allowedFees?.[selectedPlan.id]?.includes(f.id) : true
	);
	const currentInterval = selectedPlanAvailableIntervals?.find(interval => interval.id === parsedAnswers.feeId);
	const autoRenewSelection = fieldMeta.showAutoRenew
		? { autoRenew: parsedAnswers.autoRenew ?? !!fieldMeta.defaultToAutoRenew }
		: { autoRenew: !!fieldMeta.defaultToAutoRenew };
	const membershipLabel =
		selectedPlan && currentInterval
			? selectedPlan.name +
				' - ' +
				((currentInterval.units ?? 0) > 0
					? intervalDisplayShort(currentInterval.interval, currentInterval.units)
					: INTERVAL_NAMESLY[currentInterval?.interval])
			: '';

	const currentAnswerRef = useRef<SubscriptionAnswer | null>(null);

	const {
		data: prorate,
		isLoading: prorateLoading
		// isError: prorateError
	} = useQuery({
		queryKey: ['proratefee', currentInterval?.id, token],
		queryFn: async () => {
			console.log('running prorate');
			if (!currentInterval?.prorate) return null;
			const result = await prorateFee({ feeId: currentInterval?.id, token });
			if ((result ?? 0) > 0 && currentAnswerRef.current) {
				fillAnswer(undefined, undefined, result);
			}
			return result;
		},
		cacheTime: 0,
		staleTime: Infinity,
		enabled: formRenderMode === FormRenderMode.runtime ? !!token || inLocalSurveyMode : true
	});

	const subscriptionTotal = calculateLineItemsTotal(currentAnswer?.data?.['lineItems'] as FormFinancialLineItem[]);
	const allPlanOptions = findAllPlanOptions(plans ?? []);
	const availablePlanOptions =
		selectedPlan?.planKey !== undefined
			? (fieldMeta.allowedOptions?.[selectedPlan?.planKey]?.map(o => allPlanOptions?.find(option => option.id === o)) ?? []).filter(o =>
					checkIsCompatiblePlanOption(currentInterval, o)
				)
			: [];

	// If there is a condition check it matches the required value before rendering the question
	const conditionValid = useConditionEvaluator(props);

	// TODO - Not use effect. Make initialAnswers on block
	// This is to auto fill in the default value selected in design mode
	useEffect(() => {
		if (hasSuccessfulPayment) return;
		const defaultPlan = plans?.find(p => p.id === fieldMeta.defaultPlanId);
		if (currentAnswer || !defaultPlan || !conditionValid) return;

		const allowedFees = getAllowedFees(fieldMeta, defaultPlan);
		// Match to existing interval or first available if only one exists
		const newFee = allowedFees.length === 1 ? allowedFees[0] : null;

		const optionIds =
			fieldMeta.defaultOptions?.[defaultPlan.planKey]?.filter(
				x =>
					newFee &&
					checkIsCompatiblePlanOption(
						newFee,
						allPlanOptions.find(po => po.id == x)
					)
			) ?? [];
		const newAnswer: SubscriptionAnswer = {
			feeId: newFee?.id,
			planKey: defaultPlan.planKey,
			optionIds
		};
		fillAnswer(newAnswer);
	}, [plans, conditionValid]);

	const fillAnswer = (newAnswer: SubscriptionAnswer | undefined, autoRenewChange?: boolean, prorateArg?: CurrencyMinor | null) => {
		if (formRenderMode === FormRenderMode.design) {
			return;
		}

		const updatedAnswer: SubscriptionAnswer = {
			...(newAnswer ?? currentAnswerRef.current ?? {}), // (😱)
			...(autoRenewChange ? {} : autoRenewSelection),
			defaultSubCreateStatus: fieldMeta.defaultSubCreateStatus
		};
		currentAnswerRef.current = updatedAnswer;

		answerQuestion({
			answer: {
				answerJson: JSON.stringify(updatedAnswer)
			},
			questionName: props.name,
			type: 'Subscription',
			data: buildProvidedData(updatedAnswer, plans ?? [], fieldMeta.immediatePayment, prorate ?? prorateArg)
		});
	};

	if (!conditionValid) {
		return null;
	}

	if ((plansError || currenciesError) && formRenderMode !== FormRenderMode.design)
		return <MuiAlert severity="error">There has been an error loading financial information.</MuiAlert>;
	if (hasSuccessfulPayment)
		return (
			<MuiAlert severity="success">
				<Typography>Payment has been processed. Subscription types can no longer be changed.</Typography>
				{membershipLabel}
			</MuiAlert>
		);

	const allowedOptions = selectedPlan?.planKey !== undefined ? fieldMeta.allowedOptions?.[selectedPlan?.planKey] : undefined;
	const forcedOptions = (
		!allowedOptions || allowedOptions.length === 0 ? parsedAnswers.optionIds : parsedAnswers.optionIds?.filter(f => !allowedOptions.includes(f))
	)?.map(f => allPlanOptions.find(o => o.id === f));

	const showAvailableOptions =
		(selectedPlan?.planKey !== undefined && fieldMeta.allowedOptions ? fieldMeta.allowedOptions?.[selectedPlan.planKey]?.length > 0 : false) ||
		formRenderMode === FormRenderMode.design;
	return (
		<FlexColumnSpaced style={{ pointerEvents: formRenderMode !== FormRenderMode.design ? 'all' : 'none' }}>
			{(!currenciesLoading && !plansLoading) || formRenderMode === FormRenderMode.design ? (
				<>
					<SpaceEvenRowSmallGap>
						<Select<Plan, false, false, false>
							key={selectedPlan?.id}
							fullWidth
							size="small"
							loading={plansLoading}
							label={
								<span>
									{fieldMeta.labelOverrides.planKey ?? 'Select plan type'}
									{fieldMeta.required ? <RequiredAsterisk /> : null}
								</span>
							}
							value={selectedPlan}
							onChange={(_, value) => {
								const allowedFees = getAllowedFees(fieldMeta, value);
								// Match to existing interval or first available if only one exists
								const newInterval =
									allowedFees.find(f => f.interval === currentInterval?.interval) ??
									(allowedFees.length === 1 ? allowedFees[0] : undefined);

								const newOptions = findNewOptions(allPlanOptions, fieldMeta, parsedAnswers, newInterval, value ?? undefined);

								const newAnswer: SubscriptionAnswer = {
									...parsedAnswers,
									optionIds: newOptions,
									feeId: newInterval ? newInterval.id : undefined,
									planKey: value?.planKey
								};
								if (!newInterval) delete newAnswer.feeId;
								fillAnswer(newAnswer, undefined, prorate);
							}}
							options={allowedPlans}
							getOptionLabel={(o: Plan) => o.name}
						/>
						<div style={{ width: '100%' }}>
							{(selectedPlan && selectedPlanAvailableIntervals?.length > 1) || formRenderMode === FormRenderMode.design ? (
								<Select<Fee, false, false, false>
									key={currentInterval?.id}
									fullWidth
									size="small"
									label={
										<span>
											{fieldMeta.labelOverrides.interval ?? 'Select interval'}
											{fieldMeta.required ? <RequiredAsterisk /> : null}
										</span>
									}
									getOptionLabel={o => {
										return (o.units ?? 0 > 0) ? intervalDisplayShort(o.interval, o.units) : INTERVAL_NAMESLY[o.interval];
									}}
									options={selectedPlanAvailableIntervals}
									value={(currentInterval ?? null) as Fee}
									onChange={(_, v) => {
										const newInterval = v ?? undefined;
										const newOptions = findNewOptions(allPlanOptions, fieldMeta, parsedAnswers, newInterval);
										const newAnswer: SubscriptionAnswer = {
											...parsedAnswers,
											optionIds: newOptions,
											feeId: newInterval?.id
										};
										if (!newInterval) delete newAnswer.feeId;

										fillAnswer(newAnswer, undefined, prorate);
									}}
								/>
							) : null}
						</div>
					</SpaceEvenRowSmallGap>
					{showAvailableOptions ? (
						<PlanOptionsSelector
							availablePlanOptions={availablePlanOptions}
							allPlanOptions={allPlanOptions}
							parsedAnswers={parsedAnswers}
							fillAnswer={fillAnswer}
							labelStyles={labelStyles}
							answerStyles={answerStyles}
							fieldMeta={fieldMeta}
						/>
					) : null}
					{forcedOptions?.length ? (
						<>
							{!showAvailableOptions ? (
								<Typography sx={labelStyles}>{fieldMeta.labelOverrides?.PLAN_OPTION_LABEL ?? 'Plan Options:'}</Typography>
							) : null}

							{forcedOptions.map(option => (
								<Typography>
									{fieldMeta.labelOverrides?.[encodePlanOptionLabelId(option.id)] ?? createPlanOptionLabel(option)}
								</Typography>
							))}
						</>
					) : null}
				</>
			) : (
				<SpaceEvenRowSmallGap>
					<Skeleton width="50%" height={'3rem'} />
					<Skeleton width="50%" height={'3rem'} />
				</SpaceEvenRowSmallGap>
			)}
			{typeof currentInterval?.prorate !== 'undefined' && typeof prorate === 'number' ? (
				<MuiAlert severity="info" color="info">
					Your payment today is <b>{formatCurrencyFromMinorUnits(prorate, currencies?.default)}</b>, reflecting a pro-rata amount for the
					remainder of the year. The standard annual fee is <b>{formatCurrencyFromMinorUnits(currentInterval.fee, currencies?.default)}</b>.
				</MuiAlert>
			) : null}

			{fieldMeta.showAutoRenew ? (
				<FormControl>
					<MuiRadioGroup
						key={parsedAnswers.autoRenew ? 'true' : 'false'}
						value={parsedAnswers.autoRenew !== undefined ? String(parsedAnswers.autoRenew) : null}
						onChange={(_, v) => {
							const autoRenew = v === 'true';
							const newAnswer: SubscriptionAnswer = {
								...parsedAnswers,
								autoRenew
							};

							fillAnswer(newAnswer, true);
						}}
					>
						<Typography sx={labelStyles}>
							Would you like to have your membership renewed at the chosen interval automatically?
							{fieldMeta.required ? <RequiredAsterisk /> : null}
						</Typography>
						<FormControlLabel sx={answerStyles} label="Yes" control={<MuiRadio size="small" value={true} title="Yes" />} />
						<FormControlLabel sx={answerStyles} label="No" control={<MuiRadio size="small" value={false} title="No" />} />
					</MuiRadioGroup>
				</FormControl>
			) : (
				<></>
			)}
			<SpaceBetweenRow>
				<Typography sx={labelStyles}>{membershipLabel}</Typography>
				{/* There are helpers to do this that live in financial, maybe move to a helpers file in @fox/finance */}
				<Typography sx={labelStyles}>
					Cost: {formatCurrencyFromMinorUnits(BrandMath.max(subscriptionTotal, ZERO_CENTS), currencies?.default)}
				</Typography>
			</SpaceBetweenRow>
		</FlexColumnSpaced>
	);
};

const PlanOptionsSelector = ({
	availablePlanOptions,
	allPlanOptions,
	parsedAnswers,
	fillAnswer,
	labelStyles,
	answerStyles,
	fieldMeta
}: {
	availablePlanOptions: Plan['options'];
	allPlanOptions: Plan['options'];
	parsedAnswers: SubscriptionAnswer;
	labelStyles: BuildStylesType['labelStyles'];
	answerStyles: BuildStylesType['answerStyles'];
	fillAnswer: (newAnswer: SubscriptionAnswer, autoRenewChange?: boolean) => void;
	fieldMeta: SubscriptionFieldMeta;
}) => {
	const displayType = fieldMeta.optionDisplayType;
	if (allPlanOptions?.length <= 0) return null;
	switch (displayType) {
		case 'Select':
			return (
				<Select<Plan['options'][0], true, false, false>
					fullWidth
					size="small"
					label={fieldMeta.labelOverrides?.PLAN_OPTION_LABEL ?? 'Select options'}
					getOptionLabel={o => {
						return fieldMeta.labelOverrides?.[encodePlanOptionLabelId(o.id)] ?? createPlanOptionLabel(o);
					}}
					multiple={true}
					options={availablePlanOptions ?? []}
					value={parsedAnswers?.optionIds?.map(o => allPlanOptions.find(option => option.id === o)) ?? []}
					onChange={(_, v) => {
						const newAnswer: SubscriptionAnswer = {
							...parsedAnswers,
							optionIds: v?.map(option => option.id) ?? []
						};
						fillAnswer(newAnswer);
					}}
				/>
			);
		case 'Checkbox':
		default:
			return (
				<div>
					{availablePlanOptions?.length > 0 ? (
						<Typography sx={labelStyles}>{fieldMeta.labelOverrides?.PLAN_OPTION_LABEL ?? 'Plan Options:'}</Typography>
					) : null}
					<FlexColumn>
						{availablePlanOptions?.map(option => {
							return (
								<LabeledCheckbox
									label={fieldMeta.labelOverrides?.[encodePlanOptionLabelId(option.id)] ?? createPlanOptionLabel(option)}
									key={option.id}
									sx={answerStyles}
									checked={parsedAnswers?.optionIds?.includes(option.id)}
									onChange={(e, checked) => {
										let newOptions: number[] = parsedAnswers.optionIds ?? [];
										if (!checked && newOptions.includes(option.id)) {
											newOptions = newOptions.filter(o => o !== option.id);
										} else if (checked && !newOptions.includes(option.id)) newOptions.push(option.id);

										const newAnswer: SubscriptionAnswer = {
											...parsedAnswers,
											optionIds: newOptions
										};
										fillAnswer(newAnswer, undefined);
									}}
								/>
							);
						})}
					</FlexColumn>
				</div>
			);
	}
};

export const buildSubscriptionBlockRuntime = (token?: string) => {
	const block: IBlockDefinition = {
		Component: pp => {
			return <SubscriptionBlock {...pp} token={token} />;
		},
		type: SUBSCRIPTION_BLOCK_TYPE
	};
	return block;
};
